/**
 * The player moor flow (M10 P1e2, hull tiers spec §6.4, design critique #9 "moored = docked"): a hull too big for
 * the station's bay (an L at today's M bays) asks to moor, flies itself to its mooring-ring point, holds there, and
 * is then docked for every purpose (the same completion as a bay dock: phase `docked`, the clamps, the `docked`
 * event, so the career's docked flow, the station screens, ship switching and the held sim all follow).
 *
 * - **The side state** (`MoorFlowState`: station, slot, phase, hold timer) lives here and in the docking runtime's
 *   own `DockingState`, never on `DockingReadout` or `DockingRuntime` (m8e's `space/contracts.ts`). The HUD hears
 *   about the moor through the `moorGranted` and `moored` space events.
 * - **Phases** (`MOOR_PHASE`): `approach` (flying to the ring point), `holding` (inside `holdToleranceM` and under
 *   `holdSpeedMps`, the hold timer running), `moored` (docked at the ring). `none` otherwise.
 * - **The control step** (`stepMoorFlight`) writes the ship's `Control` exactly as auto-dock and the AI pilots do
 *   (D-013): no teleports, no forces. Position → desired world velocity → translation stick (flight assist on: the
 *   stick is the ship-local desired velocity over `maxSpeed`), and the nose turned toward the desired velocity with
 *   `ai/steering.ts steerTo` (`docking.json autopilot.turnGain`/`rollGain`).
 *   - **Arrive braking:** speed = min(`moor.approachSpeedMps`, `autopilot.lateralGain` × distance, √(2·s·a × distance)),
 *     where a is the def's weakest linear acceleration (thrust ÷ mass over forward, reverse, lateral and vertical) and
 *     s is `moor.brakeAccelShare`. √(2·s·a·d) is the speed from which braking at s·a stops in d, so the profile asks
 *     for at most that share of the weakest axis's thrust (0.5 [P]: half, the rest left to flight assist's lag); it
 *     holds for any hull, an L's thin thrust included.
 *   - **Station avoidance:** while the straight line to the ring point passes within the station's clearance sphere
 *     (station radius + `autopilot.stationMargin`), it flies auto-dock's tangent detour (`detourVelocity`) instead.
 *   - Under `holdSpeedMps` of desired speed it stops turning (pitch, yaw and roll at zero) and lets flight assist
 *     settle the ship.
 * - **The hold step** (`stepMoorHold`, physics stage, post-step state) counts the time inside the hold window and
 *   says when `dock.holdSeconds` is reached; the docking runtime then completes the dock with `moored` set.
 *
 * Every function here runs per tick: allocation-free, scratch in the state (D-014). Positions are float64 world
 * metres (D-011).
 */
import type { Quat, Vec3 } from '../core/math'
import { SteerArgs, steerTo } from '../ai/steering'
import type { DockingConfig } from '../data/m2Schemas'
import type { ShipDef } from '../data/schemas'
import { detourVelocity, segmentBlocked, velocityStick, zeroControl } from '../docking/autopilot'
import { moorRingPoint } from '../docking/moor'
import type { MoorConfig } from '../docking/moorConfig'
import type { ControlInput } from '../flight/flightModel'
import { Control } from '../world/components'
import type { BayFrame } from './contracts'

export const MOOR_PHASE = { none: 0, approach: 1, holding: 2, moored: 3 } as const
export type MoorPhase = (typeof MOOR_PHASE)[keyof typeof MOOR_PHASE]

/** The player's moor side state (one per docking runtime). */
export interface MoorFlowState {
  phase: MoorPhase
  /** The station moored at or flying to (-1 when none). */
  station: number
  /** The ring slot (`moor.json playerSlot`). */
  slot: number
  /** Seconds inside the hold window so far. */
  holdTime: number
  /** The pilot's `Ship.faOn` before the moor flight forced it on. */
  faBefore: number
  /** The ring point of the last step (float64 world). */
  readonly target: Vec3
  /** The desired world velocity of the last step (m/s). */
  readonly desiredVel: Vec3
  // Scratch (D-014).
  readonly detourPoint: Vec3
  readonly steer: SteerArgs
  readonly stick: ControlInput
}

export function createMoorFlowState(): MoorFlowState {
  return {
    phase: MOOR_PHASE.none,
    station: -1,
    slot: 0,
    holdTime: 0,
    faBefore: 1,
    target: { x: 0, y: 0, z: 0 },
    desiredVel: { x: 0, y: 0, z: 0 },
    detourPoint: { x: 0, y: 0, z: 0 },
    steer: new SteerArgs(),
    stick: { surge: 0, sway: 0, heave: 0, pitch: 0, yaw: 0, roll: 0 },
  }
}

/** Starts a moor flight to `slot` of `station`, remembering the pilot's flight-assist setting `faOn`. */
export function startMoor(flow: MoorFlowState, station: number, slot: number, faOn: number): void {
  flow.phase = MOOR_PHASE.approach
  flow.station = station
  flow.slot = slot
  flow.holdTime = 0
  flow.faBefore = faOn
  flow.desiredVel.x = 0
  flow.desiredVel.y = 0
  flow.desiredVel.z = 0
}

/** Back to no moor. */
export function clearMoor(flow: MoorFlowState): void {
  flow.phase = MOOR_PHASE.none
  flow.station = -1
  flow.holdTime = 0
}

/** Marks the ship moored (docked at the ring) at `station`. */
export function setMoored(flow: MoorFlowState, station: number, slot: number): void {
  flow.phase = MOOR_PHASE.moored
  flow.station = station
  flow.slot = slot
  flow.holdTime = 0
}

/** True while the moor autopilot flies the ship (approach or holding). */
export function moorFlying(flow: Readonly<MoorFlowState>): boolean {
  return flow.phase === MOOR_PHASE.approach || flow.phase === MOOR_PHASE.holding
}

/** The def's weakest linear acceleration (m/s²): the least of forward, reverse, lateral and vertical thrust over mass. */
export function weakestAccel(def: Readonly<Pick<ShipDef, 'mass' | 'thrustForward' | 'thrustReverse' | 'thrustLateral' | 'thrustVertical'>>): number {
  let t = def.thrustForward
  if (def.thrustReverse < t) t = def.thrustReverse
  if (def.thrustLateral < t) t = def.thrustLateral
  if (def.thrustVertical < t) t = def.thrustVertical
  return t / def.mass
}

/** The arrive-braking speed at `distance` from the ring point (module doc). */
export function moorArriveSpeed(distance: number, accel: number, approachSpeed: number, gain: number, brakeShare: number): number {
  const d = distance > 0 ? distance : 0
  let v = approachSpeed
  const g = gain * d
  if (g < v) v = g
  const b = Math.sqrt(2 * brakeShare * accel * d)
  return b < v ? b : v
}

export type MoorFlightDef = Pick<ShipDef, 'maxSpeed' | 'mass' | 'thrustForward' | 'thrustReverse' | 'thrustLateral' | 'thrustVertical'>
export type MoorFlightConfig = Pick<MoorConfig, 'ringRadiusM' | 'ringClearM' | 'slots' | 'corridorClearDeg' | 'approachSpeedMps' | 'brakeAccelShare' | 'holdSpeedMps'>

/**
 * One control step of the moor flight for ship `eid` (float64 `pos`, world `vel`, rotation `rot`) toward its ring
 * point round a station at `stationPos` (bounding radius `stationRadius`, bay `frame`): writes all six `Control`
 * axes of `eid` (boost off) and leaves the ring point in `flow.target`. Flight assist must be on.
 */
export function stepMoorFlight(
  flow: MoorFlowState,
  eid: number,
  pos: Readonly<Vec3>,
  rot: Readonly<Quat>,
  stationPos: Readonly<Vec3>,
  stationRadius: number,
  frame: Readonly<BayFrame>,
  def: Readonly<MoorFlightDef>,
  cfg: Readonly<MoorFlightConfig>,
  ap: Readonly<DockingConfig['autopilot']>,
): void {
  const t = moorRingPoint(stationPos, stationRadius, frame.out, flow.slot, cfg, flow.target)
  const v = flow.desiredVel
  const dx = t.x - pos.x
  const dy = t.y - pos.y
  const dz = t.z - pos.z
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  const clearance = stationRadius + ap.stationMargin
  if (segmentBlocked(pos.x, pos.y, pos.z, t.x, t.y, t.z, stationPos, clearance)) {
    detourVelocity(pos.x, pos.y, pos.z, stationPos, clearance, t, frame, cfg.approachSpeedMps, ap.lateralGain, v, flow.detourPoint)
  } else if (d > 0) {
    const k = moorArriveSpeed(d, weakestAccel(def), cfg.approachSpeedMps, ap.lateralGain, cfg.brakeAccelShare) / d
    v.x = dx * k
    v.y = dy * k
    v.z = dz * k
  } else {
    v.x = 0
    v.y = 0
    v.z = 0
  }
  const speed = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z)
  if (speed > cfg.holdSpeedMps) {
    const a = flow.steer
    a.dx = v.x
    a.dy = v.y
    a.dz = v.z
    a.turnGain = ap.turnGain
    a.rollGain = ap.rollGain
    steerTo(eid, a)
  } else {
    Control.pitch[eid] = 0
    Control.yaw[eid] = 0
    Control.roll[eid] = 0
  }
  const s = velocityStick(rot, v, def.maxSpeed, zeroControl(flow.stick))
  Control.surge[eid] = s.surge
  Control.sway[eid] = s.sway
  Control.heave[eid] = s.heave
  Control.boost[eid] = 0
}

/**
 * One hold step (physics stage, post-step state): inside `holdToleranceM` of `flow.target` and under `holdSpeedMps`
 * the timer runs (phase `holding`), else it resets (phase `approach`). True when it reaches `holdSeconds`.
 */
export function stepMoorHold(
  flow: MoorFlowState,
  pos: Readonly<Vec3>,
  vel: Readonly<Vec3>,
  cfg: Readonly<Pick<MoorConfig, 'holdToleranceM' | 'holdSpeedMps'>>,
  holdSeconds: number,
  dt: number,
): boolean {
  const t = flow.target
  const ox = t.x - pos.x
  const oy = t.y - pos.y
  const oz = t.z - pos.z
  const d = Math.sqrt(ox * ox + oy * oy + oz * oz)
  const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z)
  if (d <= cfg.holdToleranceM && speed < cfg.holdSpeedMps) {
    flow.phase = MOOR_PHASE.holding
    flow.holdTime += dt
  } else {
    flow.phase = MOOR_PHASE.approach
    flow.holdTime = 0
  }
  return flow.holdTime >= holdSeconds
}
